// frontend/src/utils/context/buildContextEnvelope.js

const CONTEXT_SCHEMA_VERSION = 'daisy.context.v1';
const SESSION_STORAGE_KEY = 'daisy-context-session-id';

const MAX_STRING_LENGTH = 2000;
const MAX_ARRAY_LENGTH = 12;
const MAX_OBJECT_KEYS = 30;
const MAX_DEPTH = 5;
const MAX_RECENT_MESSAGES = 8;
const MAX_RECALLED_MEMORIES = 6;
const MAX_WEB_RESULTS = 5;
const MAX_RELATIONSHIPS = 100;
const MAX_RELATIONSHIP_EDGES = 250;

const BLOCKED_KEYS = new Set([
  'shadowId',
  'shadow_id',
  'userId',
  'user_id',
  'accountId',
  'account_id',
  'email',
  'token',
  'password',
  'password_hash',
]);

function truncateString(value = '', maxLength = MAX_STRING_LENGTH) {
  const text = String(value || '').trim();
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1)}…`;
}

function clampNumber(value, min = 0, max = 1) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.max(min, Math.min(max, numeric));
}

function createId(prefix = 'context') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function getOrCreateSessionId() {
  if (typeof window === 'undefined') {
    return createId('session');
  }

  try {
    const storage = window.sessionStorage || window.localStorage;
    const existing = storage?.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;

    const created = createId('session');
    storage?.setItem(SESSION_STORAGE_KEY, created);
    return created;
  } catch (err) {
    return createId('session');
  }
}

function sanitizeJson(value, depth = 0) {
  if (value === null || value === undefined) return null;
  if (depth > MAX_DEPTH) return null;

  if (typeof value === 'string') {
    return truncateString(value);
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_ARRAY_LENGTH)
      .map((item) => sanitizeJson(item, depth + 1))
      .filter((item) => item !== null && item !== undefined);
  }

  if (typeof value === 'object') {
    const output = {};
    const entries = Object.entries(value)
      .filter(([key]) => !BLOCKED_KEYS.has(key) && !String(key).startsWith('_'))
      .slice(0, MAX_OBJECT_KEYS);

    entries.forEach(([key, item]) => {
      const sanitized = sanitizeJson(item, depth + 1);
      if (sanitized !== null && sanitized !== undefined && sanitized !== '') {
        output[key] = sanitized;
      }
    });

    return Object.keys(output).length ? output : null;
  }

  return null;
}

function sanitizeRecentMessages(messages = []) {
  if (!Array.isArray(messages)) return [];

  return messages
    .slice(-MAX_RECENT_MESSAGES)
    .map((message) => {
      const sender = message?.sender === 'bot' ? 'bot' : 'user';
      const text = truncateString(message?.text || '', 3000);
      if (!text) return null;
      return { sender, text };
    })
    .filter(Boolean);
}

function sanitizeRecalledMemories(memories = []) {
  if (!Array.isArray(memories)) return [];

  return memories
    .slice(0, MAX_RECALLED_MEMORIES)
    .map((item) => truncateString(item, 1800))
    .filter(Boolean);
}

function sanitizeWebResults(results = null) {
  const list = Array.isArray(results)
    ? results
    : results
      ? [results]
      : [];

  return list
    .slice(0, MAX_WEB_RESULTS)
    .map((item) => sanitizeJson(item))
    .filter(Boolean);
}


function sanitizeRelationships(relationships = []) {
  if (!Array.isArray(relationships)) return [];

  return relationships
    .filter((record) => record && record.isActive !== false)
    .slice(0, MAX_RELATIONSHIPS)
    .map((record) => ({
      entityId: truncateString(record.entityId || '', 120) || null,
      first_name: truncateString(record.first_name || '', 160),
      last_name: truncateString(record.last_name || '', 160) || null,
      relationship: truncateString(record.relationship || '', 120),
      lifeState: truncateString(record.lifeState || 'unknown', 80),
      healthState: truncateString(record.healthState || 'unknown', 80),
      location: truncateString(record.location || '', 240) || null,
      source: truncateString(record.source || '', 120) || null,
      isActive: record.isActive !== false,
      updatedAt: truncateString(record.updatedAt || '', 80) || null,
    }))
    .filter((record) => record.first_name && record.relationship);
}

function sanitizeRelationshipNetwork(relationshipNetwork = null) {
  const edges = Array.isArray(relationshipNetwork?.edges)
    ? relationshipNetwork.edges
    : Array.isArray(relationshipNetwork)
      ? relationshipNetwork
      : [];

  return {
    edges: edges
      .filter((edge) => edge && edge.isActive !== false)
      .slice(0, MAX_RELATIONSHIP_EDGES)
      .map((edge) => ({
        edgeId: truncateString(edge.edgeId || '', 120) || null,
        logicalKey: truncateString(edge.logicalKey || '', 420) || null,
        fromEntityId: truncateString(edge.fromEntityId || '', 120) || null,
        fromName: truncateString(edge.fromName || '', 160),
        fromType: truncateString(edge.fromType || 'unknown', 100),
        fromIsRoot: edge.fromIsRoot === true,
        toEntityId: truncateString(edge.toEntityId || '', 120) || null,
        toName: truncateString(edge.toName || '', 160),
        toType: truncateString(edge.toType || 'unknown', 100),
        toIsRoot: edge.toIsRoot === true,
        relation: truncateString(edge.relation || '', 140),
        status: truncateString(edge.status || 'observed', 100),
        confidence: clampNumber(edge.confidence),
        source: truncateString(edge.source || '', 120) || null,
        isActive: edge.isActive !== false,
        updatedAt: truncateString(edge.updatedAt || '', 80) || null,
      }))
      .filter((edge) => edge.fromName && edge.toName && edge.relation),
  };
}

function buildSafeIdentity(activeIdentity = null, effectiveUserName = '') {
  return {
    displayName: truncateString(
      activeIdentity?.displayName || effectiveUserName || 'Friend',
      120
    ),
    status: truncateString(activeIdentity?.status || 'unknown', 40),
    confidence: clampNumber(activeIdentity?.confidence),
    hydrationSource: truncateString(activeIdentity?.hydrationSource || 'unknown', 40),
    sourceConsistency:
      activeIdentity?.signals?.sourceConsistency === true
        ? true
        : activeIdentity?.signals?.sourceConsistency === false
          ? false
          : null,
  };
}

function buildContextEnvelope({
  promptMemory = {},
  conversationState = {},
  intentInfo = {},
  detectedContext = {},
  recalledMemories = [],
  webSearchResult = null,
  activeIdentity = null,
  effectiveUserName = '',
  memoryName = null,
  relationships = [],
  relationshipNetwork = null,
} = {}) {
  const sessionId = getOrCreateSessionId();
  const turnId = createId('turn');

  const intentState =
    conversationState?.intentState || promptMemory?.intentState || null;

  return {
    schemaVersion: CONTEXT_SCHEMA_VERSION,
    generatedAt: new Date().toISOString(),
    source: 'frontend_observation',
    session: {
      sessionId,
      turnId,
    },
    identity: buildSafeIdentity(activeIdentity, effectiveUserName),
    relationalState: {
      relationships: sanitizeRelationships(relationships),
      relationshipNetwork: sanitizeRelationshipNetwork(relationshipNetwork),
    },
    intent: {
      intent: truncateString(intentInfo?.intent || 'unknown', 80),
      target: truncateString(intentInfo?.target || '', 160) || null,
      memoryName: truncateString(memoryName || intentInfo?.memoryName || '', 160) || null,
      source: truncateString(intentInfo?.source || 'unknown', 80),
      confidence: clampNumber(intentInfo?.confidence),
    },
    state: {
      frame: sanitizeJson(conversationState?.frame || promptMemory?.frame || null),
      control: sanitizeJson(conversationState?.control || null),
      reference: sanitizeJson(conversationState?.reference || null),
      entityState: sanitizeJson(conversationState?.entityState || null),
      topicState: sanitizeJson(conversationState?.topicState || null),
      objectiveDecisionState: sanitizeJson(
        conversationState?.objectiveDecisionState ||
          promptMemory?.objectiveDecisionState ||
          null
      ),
      intentState: sanitizeJson(intentState),
      pendingQuestion: sanitizeJson(
        conversationState?.pendingQuestion || promptMemory?.pendingQuestion || null
      ),
      pendingQuestionState: sanitizeJson(
        conversationState?.pendingQuestionState || null
      ),
      turnAttachment: sanitizeJson(conversationState?.turnAttachment || null),
      turnSignalArbitration: sanitizeJson(
        conversationState?.turnSignalArbitration ||
          promptMemory?.turnSignalArbitration ||
          null
      ),
      relationshipReferenceState: sanitizeJson(
        conversationState?.relationshipReferenceState || null
      ),
      activeFrameState: sanitizeJson(
        conversationState?.activeFrameState || null
      ),
      admissibilityState: sanitizeJson(
        conversationState?.admissibilityState || null
      ),
      lifecycle: sanitizeJson(conversationState?.lifecycle || null),
    },
    detectedContext: sanitizeJson(detectedContext || null),
    memory: {
      summary: truncateString(promptMemory?.summary || '', 3000) || null,
      recalled: sanitizeRecalledMemories(recalledMemories),
    },
    retrieval: {
      webSearchResults: sanitizeWebResults(webSearchResult),
    },
    recentMessages: sanitizeRecentMessages(conversationState?.recentMessages || []),
  };
}

export {
  CONTEXT_SCHEMA_VERSION,
  buildContextEnvelope,
};

export default buildContextEnvelope;
